import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import Layout from "@/components/Layout";
import TeamLogo from "@/components/TeamLogo";
import { useTeam } from "@/context/TeamContext";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, UsersThree, CalendarBlank, MapPin, House, Airplane, SoccerBall, ArrowRight, GearSix, Trophy, UploadSimple, CaretDown, Plus, Check, Trash, ChartBar, CheckCircle } from "@phosphor-icons/react";

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const item = { hidden: { opacity: 0, y: 10 }, show: { opacity: 1, y: 0 } };

export default function Dashboard() {
  const navigate = useNavigate();
  const { teams, currentTeamId, currentRole, setCurrentTeamId, refreshTeams, loading: teamsLoading } = useTeam();
  const isViewer = currentRole === "viewer";
  const isMember = currentRole === "member";
  const canManage = !isViewer && !isMember;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ name: "", age_group: "", season: "" });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");
  const [showDeleteTeam, setShowDeleteTeam] = useState(false);
  const [deletingTeam, setDeletingTeam] = useState(false);

  const load = useCallback(async () => {
    if (!currentTeamId) return;
    try {
      const res = await api.get(`/teams/${currentTeamId}/dashboard`);
      setData(res.data);
    } catch (e) {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [currentTeamId]);

  useEffect(() => {
    if (teamsLoading) return;
    if (teams.length === 0) {
      navigate("/setup");
      return;
    }
    if (currentTeamId) {
      setLoading(true);
      load();
    }
  }, [teamsLoading, teams.length, currentTeamId, load, navigate]);

  const openEdit = () => {
    setForm({ name: data.team.name, age_group: data.team.age_group, season: data.team.season });
    setLogoFile(null);
    setLogoPreview(null);
    setEditError("");
    setEditOpen(true);
  };

  const saveTeam = async (e) => {
    e.preventDefault();
    setEditError("");
    setSaving(true);
    try {
      await api.put(`/teams/${currentTeamId}`, { name: form.name, age_group: form.age_group, season: form.season });
      if (logoFile) {
        const fd = new FormData();
        fd.append("file", logoFile);
        await api.post(`/teams/${currentTeamId}/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success("Team updated");
      setEditOpen(false);
      await refreshTeams();
      await load();
    } catch (err) {
      setEditError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteTeam = async () => {
    setDeletingTeam(true);
    try {
      await api.delete(`/teams/${currentTeamId}`);
      toast.success("Team deleted");
      setShowDeleteTeam(false);
      setEditOpen(false);
      const remaining = await refreshTeams();
      if (!remaining || remaining.length === 0) {
        navigate("/setup");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeletingTeam(false);
    }
  };

  if (teamsLoading || loading || !data) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-32">
          <SoccerBall size={40} className="text-[#CCFF00] animate-spin" weight="fill" />
        </div>
      </Layout>
    );
  }

  const { team, roster_count, active_count, next_game, previous_game, record } = data;
  const inputCls = "w-full bg-[#0A0A0A] border border-zinc-800 text-white px-3 py-2 rounded-sm focus:outline-none focus:border-[#CCFF00] transition-colors duration-150";

  return (
    <Layout>
      <motion.div variants={stagger} initial="hidden" animate="show">
        {/* Team header with switcher */}
        <motion.div variants={item} className="flex items-center gap-4 mb-8 flex-wrap">
          <TeamLogo teamId={currentTeamId} logoPath={team.logo_path} size={64} className="rounded-sm" />
          <div>
            <p className="font-label text-xs text-[#CCFF00] mb-1">{team.age_group} · {team.season}</p>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-4xl sm:text-5xl text-white leading-none" data-testid="dashboard-team-name">
                {team.name}
              </h1>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    data-testid="team-switcher"
                    className="flex items-center justify-center w-9 h-9 border border-zinc-800 rounded-sm text-zinc-400 hover:text-[#CCFF00] hover:border-[#CCFF00] transition-colors duration-150"
                    title="Switch team"
                  >
                    <CaretDown size={18} weight="bold" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-[#121212] border-zinc-800 text-white min-w-[220px]">
                  {teams.map((t) => (
                    <DropdownMenuItem
                      key={t.id}
                      data-testid={`team-option-${t.id}`}
                      onClick={() => setCurrentTeamId(t.id)}
                      className="cursor-pointer focus:bg-white/5 focus:text-white flex items-center justify-between"
                    >
                      <span className={t.id === currentTeamId ? "text-[#CCFF00]" : "text-zinc-200"}>{t.name}</span>
                      {t.id === currentTeamId && <Check size={16} className="text-[#CCFF00]" weight="bold" />}
                    </DropdownMenuItem>
                  ))}
                  {canManage && (
                    <DropdownMenuItem
                      data-testid="add-team-menu-item"
                      onClick={() => navigate("/setup")}
                      className="cursor-pointer focus:bg-white/5 text-[#CCFF00] flex items-center gap-2 border-t border-zinc-800 mt-1 pt-2"
                    >
                      <Plus size={16} weight="bold" /> Add a team
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {canManage && (
            <div className="ml-auto flex items-center gap-2">
              <button
                onClick={() => navigate("/setup")}
                data-testid="add-team-btn"
                className="flex items-center gap-2 bg-[#CCFF00] text-black font-label text-xs px-4 py-2 rounded-sm hover:bg-[#B3E600] hover:-translate-y-0.5 transition-all duration-150"
              >
                <Plus size={16} weight="bold" /> Add team
              </button>
              <button
                onClick={openEdit}
                data-testid="edit-team-btn"
                className="flex items-center gap-2 border border-zinc-800 text-zinc-300 hover:text-[#CCFF00] hover:border-[#CCFF00] font-label text-xs px-4 py-2 rounded-sm transition-colors duration-150"
              >
                <GearSix size={16} weight="bold" /> Edit team
              </button>
            </div>
          )}
        </motion.div>

        {/* Stats grid — 2x2 of equal-sized tiles on desktop (Season Stats /
            Next Game / Season Record / Previous Game, reading order), a
            single stacked column on mobile in Stats/Record/Next/Previous
            order. Same tile content/click-through as before — `order`
            utilities just control visual placement per breakpoint. */}
        <div className="grid grid-cols-1 md:grid-cols-2 md:auto-rows-fr gap-6 mb-8">
          <motion.div
            variants={item}
            onClick={() => navigate("/stats")}
            className="order-1 md:order-1 h-full bg-[#121212] border border-zinc-800 p-6 rounded-sm hover:border-[#CCFF00] cursor-pointer transition-colors duration-150"
            data-testid="stat-stats"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="font-label text-xs text-zinc-500">Season Stats</span>
              <ChartBar size={22} className="text-[#CCFF00]" weight="bold" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div data-testid="stats-goals-for"><p className="font-heading text-4xl text-white leading-none">{record.goals_for}</p><p className="font-label text-[10px] text-zinc-500 mt-2">Goals for</p></div>
              <div data-testid="stats-goals-against"><p className="font-heading text-4xl text-white leading-none">{record.goals_against}</p><p className="font-label text-[10px] text-zinc-500 mt-2">Goals against</p></div>
              <div data-testid="stats-goal-diff"><p className={`font-heading text-4xl leading-none ${record.goal_diff > 0 ? "text-[#32D74B]" : record.goal_diff < 0 ? "text-[#FF3B30]" : "text-white"}`}>{record.goal_diff > 0 ? "+" : ""}{record.goal_diff}</p><p className="font-label text-[10px] text-zinc-500 mt-2">Goal diff</p></div>
            </div>
          </motion.div>

          <motion.div
            variants={item}
            onClick={() => next_game && navigate(`/games/${next_game.id}`)}
            className={`order-3 md:order-2 h-full bg-[#121212] border border-zinc-800 p-6 rounded-sm transition-colors duration-150 ${next_game ? "hover:border-[#CCFF00] cursor-pointer" : ""}`}
            data-testid="stat-next-game"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-label text-xs text-zinc-500">Next game</span>
              <CalendarBlank size={22} className="text-[#CCFF00]" weight="bold" />
            </div>
            {next_game ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {next_game.is_home ? <House size={16} className="text-zinc-400" /> : <Airplane size={16} className="text-zinc-400" />}
                  <span className="font-label text-[10px] text-zinc-400">{next_game.is_home ? "Home" : "Away"}</span>
                </div>
                <p className="font-heading text-3xl text-white leading-none">vs {next_game.opponent}</p>
                <p className="text-sm text-zinc-500 mt-2">{formatDate(next_game.date)}</p>
                {next_game.location && (
                  <p className="text-xs text-zinc-600 mt-1 flex items-center gap-1">
                    <MapPin size={12} /> {next_game.location}
                  </p>
                )}
              </>
            ) : (
              <p className="text-zinc-600 text-sm mt-4">No upcoming games scheduled.</p>
            )}
          </motion.div>

          <motion.div
            variants={item}
            onClick={() => navigate("/games")}
            className="order-2 md:order-3 h-full bg-[#121212] border border-zinc-800 p-6 rounded-sm hover:border-[#CCFF00] cursor-pointer transition-colors duration-150"
            data-testid="stat-record"
          >
            <div className="flex items-center justify-between mb-6">
              <span className="font-label text-xs text-zinc-500">Season record</span>
              <Trophy size={22} className="text-[#CCFF00]" weight="bold" />
            </div>
            {record.played === 0 ? (
              <p className="text-zinc-600 text-sm" data-testid="record-empty">No results logged yet. Enter final scores on your games to build your record.</p>
            ) : (
              <div className="grid grid-cols-3 gap-4">
                <div data-testid="record-wins"><p className="font-heading text-5xl text-[#32D74B] leading-none">{record.wins}</p><p className="font-label text-[10px] text-zinc-500 mt-2">Wins</p></div>
                <div data-testid="record-draws"><p className="font-heading text-5xl text-zinc-300 leading-none">{record.draws}</p><p className="font-label text-[10px] text-zinc-500 mt-2">Draws</p></div>
                <div data-testid="record-losses"><p className="font-heading text-5xl text-[#FF3B30] leading-none">{record.losses}</p><p className="font-label text-[10px] text-zinc-500 mt-2">Losses</p></div>
              </div>
            )}
            {record.played > 0 && <p className="text-xs text-zinc-600 mt-4">{record.played} game{record.played !== 1 ? "s" : ""} played</p>}
          </motion.div>

          <motion.div
            variants={item}
            onClick={() => previous_game && navigate(`/games/${previous_game.id}`)}
            className={`order-4 md:order-4 h-full bg-[#121212] border border-zinc-800 p-6 rounded-sm transition-colors duration-150 ${previous_game ? "hover:border-[#CCFF00] cursor-pointer" : ""}`}
            data-testid="stat-previous-game"
          >
            <div className="flex items-center justify-between mb-4">
              <span className="font-label text-xs text-zinc-500">Previous game</span>
              <CheckCircle size={22} className="text-[#CCFF00]" weight="bold" />
            </div>
            {previous_game ? (
              <>
                <div className="flex items-center gap-2 mb-1">
                  {previous_game.is_home ? <House size={16} className="text-zinc-400" /> : <Airplane size={16} className="text-zinc-400" />}
                  <span className="font-label text-[10px] text-zinc-400">{previous_game.is_home ? "Home" : "Away"}</span>
                </div>
                <p className="font-heading text-3xl text-white leading-none">vs {previous_game.opponent}</p>
                <div className="flex items-center gap-3 mt-2">
                  <p className="text-sm text-zinc-500">{formatDate(previous_game.date)}</p>
                  <p
                    data-testid="previous-game-score"
                    className={`font-heading text-lg leading-none ${previous_game.score_for > previous_game.score_against ? "text-[#32D74B]" : previous_game.score_for < previous_game.score_against ? "text-[#FF3B30]" : "text-white"}`}
                  >
                    {previous_game.score_for}-{previous_game.score_against}
                  </p>
                </div>
              </>
            ) : (
              <p className="text-zinc-600 text-sm mt-4" data-testid="previous-game-empty">No games played yet.</p>
            )}
          </motion.div>
        </div>
      </motion.div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="bg-[#121212] border-zinc-800 text-white" data-testid="edit-team-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Edit team</DialogTitle>
            <DialogDescription className="text-zinc-500">Update your team details and logo.</DialogDescription>
          </DialogHeader>
          <button
            type="button"
            onClick={() => { setEditOpen(false); navigate("/admin?tab=roster"); }}
            data-testid="manage-roster-btn"
            className="w-full flex items-center justify-between gap-2 border border-zinc-800 hover:border-[#CCFF00] text-zinc-200 hover:text-[#CCFF00] font-label text-xs px-4 py-3 rounded-sm transition-colors duration-150"
          >
            <span className="flex items-center gap-2"><UsersThree size={16} weight="bold" /> Manage roster</span>
            <ArrowRight size={14} weight="bold" />
          </button>
          <form onSubmit={saveTeam} className="space-y-4">
            <div className="flex items-center gap-4">
              <label htmlFor="edit-logo-upload" className="w-16 h-16 shrink-0 border border-dashed border-zinc-700 rounded-sm flex items-center justify-center cursor-pointer hover:border-[#CCFF00] transition-colors duration-150 overflow-hidden bg-[#0A0A0A]" data-testid="edit-logo-label">
                {logoPreview ? <img src={logoPreview} alt="preview" className="w-full h-full object-cover" /> : <TeamLogo teamId={currentTeamId} logoPath={data.team.logo_path} size={64} />}
              </label>
              <div>
                <p className="font-label text-xs text-zinc-400 mb-1 flex items-center gap-2"><UploadSimple size={14} /> Swap logo</p>
                <p className="text-xs text-zinc-600">Optional. PNG or JPG.</p>
                <input id="edit-logo-upload" type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) { setLogoFile(f); setLogoPreview(URL.createObjectURL(f)); } }} data-testid="edit-logo-input" className="hidden" />
              </div>
            </div>
            <div>
              <label className="font-label text-xs text-zinc-400 block mb-2">Team name</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-team-name" className={inputCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="font-label text-xs text-zinc-400 block mb-2">Age group</label>
                <input required value={form.age_group} onChange={(e) => setForm({ ...form, age_group: e.target.value })} data-testid="edit-team-age" className={inputCls} />
              </div>
              <div>
                <label className="font-label text-xs text-zinc-400 block mb-2">Season</label>
                <input required value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} data-testid="edit-team-season" className={inputCls} />
              </div>
            </div>
            {editError && <p className="text-[#FF3B30] text-sm" data-testid="edit-team-error">{editError}</p>}
            <DialogFooter className="sm:justify-between">
              <button type="button" onClick={() => setShowDeleteTeam(true)} data-testid="delete-team-btn" className="flex items-center gap-2 text-zinc-500 hover:text-[#FF3B30] transition-colors duration-150 font-label text-xs sm:mr-auto">
                <Trash size={16} weight="bold" /> Delete team
              </button>
              <div className="flex items-center gap-2">
                <button type="button" onClick={() => setEditOpen(false)} className="font-label text-xs px-4 py-2 text-zinc-400 hover:text-white">Cancel</button>
                <button type="submit" disabled={saving} data-testid="save-team-btn" className="bg-[#CCFF00] text-black font-label text-xs px-5 py-2 rounded-sm hover:bg-[#B3E600] transition-colors duration-150 disabled:opacity-50">{saving ? "Saving…" : "Save changes"}</button>
              </div>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteTeam} onOpenChange={setShowDeleteTeam}>
        <AlertDialogContent className="bg-[#121212] border-zinc-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading text-2xl">Delete team?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This permanently deletes <span className="text-white font-semibold">{data.team.name}</span> along with its entire roster and game history. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-zinc-700 text-white hover:bg-zinc-800">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); deleteTeam(); }} disabled={deletingTeam} data-testid="confirm-delete-team" className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80">
              {deletingTeam ? "Deleting…" : "Delete team"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
